import { Button, IconButton, TextField, Typography } from '@mui/material';
import React, { FC } from 'react';
import { toast } from 'react-toastify';
import { parseEpcisPreview, serialsForProductLine } from './epcis-preview.helpers';
import { serialsFromPreview } from './serial-staging.helpers';

type Props = {
    serials: string[];
    onChange: (serials: string[]) => void;
    disabled?: boolean;
    previewJson?: string | null;
    selectedProductLineKey?: string | null;
    label?: string;
};

const InboundSerialNumbersEditor: FC<Props> = ({
    serials,
    onChange,
    disabled = false,
    previewJson,
    selectedProductLineKey,
    label,
}) => {
    const updateAt = (index: number, value: string) => {
        const next = [...serials];
        next[index] = value;
        onChange(next);
    };

    const removeAt = (index: number) => {
        onChange(serials.filter((_, i) => i !== index));
    };

    const addRow = () => {
        onChange([...serials, '']);
    };

    const resetFromFile = () => {
        if (!previewJson?.trim()) return;
        const summary = parseEpcisPreview(previewJson);
        if (summary?.multiProduct) {
            if (!selectedProductLineKey) {
                toast.warn('Select an EPCIS product line first');
                return;
            }
            const fromLine = serialsForProductLine(previewJson, selectedProductLineKey);
            if (fromLine.length === 0) {
                toast.warn('No serials for the selected product line');
                return;
            }
            onChange(fromLine);
            return;
        }
        try {
            const fromPreview = serialsFromPreview(previewJson);
            if (fromPreview.length === 0) {
                toast.warn('No serial numbers in file preview');
                return;
            }
            onChange(fromPreview);
        } catch {
            toast.warn('Could not read serial numbers from preview');
        }
    };

    return (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 md:col-span-2">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <Typography variant="subtitle2" className="font-semibold text-slate-800">
                    {label ?? `Serial numbers (${serials.filter((s) => s.trim()).length})`}
                </Typography>
                <div className="flex gap-2">
                    {previewJson ? (
                        <Button size="small" variant="text" disabled={disabled} onClick={resetFromFile}>
                            Reset from file
                        </Button>
                    ) : null}
                    <Button size="small" variant="outlined" disabled={disabled} onClick={addRow}>
                        Add row
                    </Button>
                </div>
            </div>
            <Typography variant="caption" color="text.secondary" display="block" className="mb-2">
                From commissioning SGTINs in the EPCIS file — correct before approve if needed.
            </Typography>
            {serials.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No serials yet. Save staging after the file is validated, or add rows manually.
                </Typography>
            ) : (
                <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                    {serials.map((serial, index) => (
                        <div key={`serial-${index}`} className="flex items-center gap-2">
                            <TextField
                                size="small"
                                fullWidth
                                disabled={disabled}
                                label={`Serial ${index + 1}`}
                                value={serial}
                                onChange={(e) => updateAt(index, e.target.value)}
                            />
                            <IconButton
                                size="small"
                                disabled={disabled}
                                aria-label="Remove serial"
                                onClick={() => removeAt(index)}
                            >
                                ×
                            </IconButton>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default InboundSerialNumbersEditor;
